"use client";
import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, ArrowUpRight, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { BRAND } from "../../lib/brand";

const API = "/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", telegram: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        telegram: form.telegram.trim() || null,
        message: form.message.trim(),
      });
      toast.success("Message sent! We'll be in touch soon.");
      setForm({ name: "", email: "", telegram: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to send message. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: heading + direct contact */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <p className="text-xs uppercase tracking-[0.22em] text-[#00E5FF] mb-5" data-testid="contact-label">
              Contact
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight" data-testid="contact-heading">
              Let's{" "}
              <span className="gradient-text font-medium">build your bot</span>.
            </h2>
            <p className="mt-5 text-neutral-400 max-w-md">
              Ping the owner directly on Telegram or send a project brief — most
              messages get a reply within a few hours.
            </p>

            <div className="mt-8 space-y-3">
              <a
                href={BRAND.ownerTelegram}
                target="_blank"
                rel="noreferrer"
                className="group flex items-center justify-between gap-4 p-5 rounded-2xl bg-[#0A0A0A] border border-white/10 hover:border-[#00E5FF]/40 transition-colors"
                data-testid="contact-telegram-button"
              >
                <div className="flex items-center gap-4">
                  <div className="h-11 w-11 rounded-xl bg-[#229ED9]/15 border border-[#229ED9]/30 grid place-items-center">
                    <Send className="h-5 w-5 text-[#7CCBF1]" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-neutral-500">Telegram</p>
                    <p className="text-sm font-medium">{BRAND.ownerHandle}</p>
                  </div>
                </div>
                <ArrowUpRight className="h-5 w-5 text-neutral-500 group-hover:text-white group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
              </a>

              <a
                href={BRAND.ownerEmail}
                target="_blank"
                rel="noreferrer"
                className="group flex items-center justify-between gap-4 p-5 rounded-2xl bg-[#0A0A0A] border border-white/10 hover:border-[#00E5FF]/40 transition-colors"
                data-testid="contact-email-button"
              >
                <div className="flex items-center gap-4">
                  <div className="h-11 w-11 rounded-xl bg-white/[0.04] border border-white/10 grid place-items-center">
                    <Mail className="h-5 w-5 text-[#7CCBF1]" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-neutral-500">Direct Message</p>
                    <p className="text-sm font-medium">{BRAND.ownerEmailLabel}</p>
                  </div>
                </div>
                <ArrowUpRight className="h-5 w-5 text-neutral-500 group-hover:text-white group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
              </a>
            </div>
          </motion.div>

          {/* Right: form */}
          <motion.form
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 rounded-3xl border-gradient p-7 md:p-10"
            data-testid="contact-form"
          >
            <h3 className="font-display text-2xl font-medium">Send a message</h3>
            <p className="text-sm text-neutral-400 mt-1.5">
              Share your idea — we'll come back with next steps.
            </p>

            <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-wider text-neutral-400">
                  Name
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  placeholder="Your name"
                  className="mt-2 bg-black/40 border-white/10 text-white placeholder:text-neutral-600 h-11 focus-visible:ring-[#007AFF]/40 focus-visible:border-[#007AFF]/60"
                  data-testid="contact-input-name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-wider text-neutral-400">
                  Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="you@domain.com"
                  className="mt-2 bg-black/40 border-white/10 text-white placeholder:text-neutral-600 h-11 focus-visible:ring-[#007AFF]/40 focus-visible:border-[#007AFF]/60"
                  data-testid="contact-input-email"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="telegram" className="text-xs uppercase tracking-wider text-neutral-400">
                  Telegram username
                </Label>
                <Input
                  id="telegram"
                  name="telegram"
                  value={form.telegram}
                  onChange={onChange}
                  placeholder="@yourhandle (optional)"
                  className="mt-2 bg-black/40 border-white/10 text-white placeholder:text-neutral-600 h-11 focus-visible:ring-[#007AFF]/40 focus-visible:border-[#007AFF]/60"
                  data-testid="contact-input-telegram"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="message" className="text-xs uppercase tracking-wider text-neutral-400">
                  Message
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  placeholder="Tell us about your bot idea, community size, and what you'd like to automate..."
                  rows={5}
                  className="mt-2 bg-black/40 border-white/10 text-white placeholder:text-neutral-600 focus-visible:ring-[#007AFF]/40 focus-visible:border-[#007AFF]/60 resize-none"
                  data-testid="contact-input-message"
                  required
                />
              </div>
            </div>

            <div className="mt-7 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-neutral-500">
                By submitting, you agree to be contacted regarding your inquiry.
              </p>
              <Button
                type="submit"
                disabled={loading}
                className="bg-[#007AFF] hover:bg-[#3B82F6] text-white rounded-full h-12 px-6 glow-blue"
                data-testid="contact-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send message
                    <Send className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
